type Plan = {
  name: string;
  price: string;
  suffix: string;
  blurb: string;
  features: string[];
  cta: { label: string; href: string };
  highlighted: boolean;
};

const plans: Plan[] = [
  {
    name: 'Solo',
    price: '$5',
    suffix: '/mo',
    blurb: 'For freelancers running a handful of retainers.',
    features: [
      'Up to 5 client tokens',
      'Unlimited providers',
      '30-day usage history',
      'CSV export',
      'Email support',
    ],
    cta: { label: 'Start 14-day trial', href: '/_auth/google/login?plan=solo' },
    highlighted: false,
  },
  {
    name: 'Agency',
    price: '$20',
    suffix: '/mo',
    blurb: 'For small studios with parallel client work.',
    features: [
      'Unlimited client tokens',
      'Subscription pro-rata allocation',
      '12-month usage history',
      'CSV + scheduled exports',
      'Bonsai / Harvest / Xero ready',
      'Priority support',
    ],
    cta: { label: 'Start 14-day trial', href: '/_auth/google/login?plan=agency' },
    highlighted: true,
  },
  {
    name: 'Scale',
    price: 'Talk',
    suffix: '',
    blurb: 'Multi-seat agencies, audit logs, SSO.',
    features: [
      'Everything in Agency',
      'Team seats & roles',
      'Audit log',
      'SSO (SAML / OIDC)',
      'Dedicated support',
    ],
    cta: { label: 'Contact us', href: 'mailto:hi@keyreceipt.com?subject=Key%20Receipt%20Scale%20plan' },
    highlighted: false,
  },
];

export default function Pricing() {
  return (
    <section id="pricing" className="py-24 border-t-2 border-border">
      <div className="container-ledger">
        <div className="max-w-2xl mb-16">
          <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-spray-magenta mb-4 font-bold">
            // Pricing
          </div>
          <h2 className="font-display text-4xl md:text-5xl uppercase leading-[0.95]">
            Small. Transparent. <span className="text-spray-yellow">No markup.</span>
          </h2>
          <p className="mt-5 text-muted-foreground">
            14-day free trial. Card required at signup. You pay your providers directly — we
            never sit on your spend.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {plans.map((plan) => (
            <div
              key={plan.name}
              className={`p-7 flex flex-col border-2 ${
                plan.highlighted ? 'bg-card border-primary' : 'bg-card border-border'
              }`}
              style={
                plan.highlighted
                  ? { boxShadow: '8px 8px 0 0 hsl(var(--primary))' }
                  : { boxShadow: '6px 6px 0 0 hsl(var(--border))' }
              }
            >
              <div className="flex items-baseline justify-between">
                <h3 className="font-display text-xl uppercase">{plan.name}</h3>
                {plan.highlighted && (
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 bg-spray-yellow text-primary-foreground">
                    Most chosen
                  </span>
                )}
              </div>
              <div className="mt-5 flex items-baseline gap-1">
                <span className="font-display text-5xl num">{plan.price}</span>
                <span className="text-muted-foreground font-mono">{plan.suffix}</span>
              </div>
              <p className="mt-3 text-sm text-muted-foreground">{plan.blurb}</p>

              <div className="my-6 h-0.5 bg-border" />

              <ul className="space-y-2.5 text-sm flex-1">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-2">
                    <span aria-hidden className="text-spray-yellow font-bold mt-px shrink-0">+</span>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              <a
                href={plan.cta.href}
                className={`mt-7 w-full block text-center py-3 text-xs font-bold uppercase tracking-wider transition-transform border-2 ${
                  plan.highlighted
                    ? 'bg-spray-yellow text-primary-foreground border-foreground hover:translate-y-[-2px]'
                    : 'border-border text-foreground hover:bg-muted'
                }`}
              >
                {plan.cta.label}
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
